'use client';

import { useEffect, useRef } from 'react';
import { acquirePusher, releasePusher } from '~/lib/pusherClient';

type PusherEventHandler = (event: string, data: any) => void;

/**
 * Subscribes to one Pusher channel for as long as `channel` stays the same.
 *
 * Mirrors the app's `usePusherSubscribe` and the IoT `useMQTTSubscribe`, with two differences
 * that matter here: the callback lives in a ref, so a new inline handler on every render does not
 * tear the subscription down and build it up again, and the channel name is the only dependency.
 *
 * The connection itself is shared across the tab (see `lib/pusherClient`), so several hooks are one
 * socket. A channel name starting with `private-` is authorised against the backend automatically.
 *
 * Channels carry status signals only — the payloads name a document and its state, never the data
 * itself. Anything worth reading is fetched over the authenticated API afterwards.
 */
export const usePusherSubscribe = (channel: string | null | undefined, onEvent: PusherEventHandler) => {
    const handler = useRef(onEvent);

    useEffect(() => {
        handler.current = onEvent;
    }, [onEvent]);

    useEffect(() => {
        if (!channel) return;

        let cancelled = false;
        let released = false;
        let client: any = null;

        // Exactly once, whichever of the two paths below gets there first.
        const release = () => {
            if (released) return;
            released = true;
            releasePusher();
        };

        void acquirePusher().then((instance) => {
            // No key, or unmounted before the socket came up. Either way the
            // hold taken by `acquirePusher` has to go back.
            if (!instance || cancelled) {
                release();
                return;
            }

            client = instance;
            const subscription = instance.subscribe(channel);
            subscription.bind_global((event: string, data: any) => {
                // pusher:subscription_succeeded and friends are transport noise, not our events.
                if (event.startsWith('pusher:') || event.startsWith('pusher_internal:')) return;
                handler.current(event, data);
            });
        });

        return () => {
            cancelled = true;
            // Still connecting: the handler above sees `cancelled` and releases.
            if (!client) return;

            client.unsubscribe(channel);
            release();
        };
    }, [channel]);
};

export default usePusherSubscribe;
